import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import PrimeLocaleDe from "primelocale/de.json";
import PrimeLocaleEn from "primelocale/en.json";
import {addLocale as addPrimeReactLocale, locale as setPrimeReactLocale} from "primereact/api";
import {initReactI18next} from "react-i18next";

import {format as formatDate, isDate} from "date-fns";
import {de as localeDE, enUS as localeEN} from "date-fns/locale";

import type {AppEnvironment} from "./environment";

const dateFmtLocales = {
    en: localeEN,
    de: localeDE,
};

/**
 * Initialize i18n
 * @param env app environment
 */
export function initializeI18n(env: AppEnvironment): void {
    // readiness is signalled through react-i18next, not through this promise.
    // useTranslation suspends until the resources are loaded, so the app waits for them anyway.
    void i18n
        .use(LanguageDetector)
        .use(Backend)
        .use(initReactI18next)
        .init({
            debug: env.isDevelopmentMode,
            fallbackLng: "en",
            supportedLngs: ["en", "de"],
            interpolation: {
                escapeValue: false, // not needed for react as it escapes by default
            },
            backend: {
                queryStringParams: {
                    v: env.gitCommitHash || "dev", // Force browser to download newer translation files after git changes
                },
            },
        });

    // Dynamic date-fns formatter (i18next v22+ Formatter API).
    // The format string is passed as a parameter, e.g.:
    //   {{ myDate, dateFns(format: dd.MM.yyyy HH:mm) }}
    i18n.services.formatter?.add("dateFns", (value: any, lng: string | undefined, options: Record<string, any>) => {
        const fmt = options?.format as string | undefined;
        if (isDate(value) && fmt && lng && lng in dateFmtLocales) {
            const locale = dateFmtLocales[lng as keyof typeof dateFmtLocales];
            return formatDate(value, fmt, {locale});
        }
        return typeof value === "string" ? value : String(value);
    });

    // PrimeReact Locale Settings
    addPrimeReactLocale("de", PrimeLocaleDe.de);
    addPrimeReactLocale("en", PrimeLocaleEn.en);
    i18n.on("languageChanged", (lng) => setPrimeReactLocale(lng));

    i18n.on("failedLoading", (lng, ns, msg) =>
        console.error(`i18n: loading namespace "${ns}" for language "${lng}" failed`, msg),
    );
}

/**
 * Custom error which contains required data for i18n error message
 */
export class ErrorI18n extends Error {
    messageArgs: Record<string, any> | null;

    /**
     * Ob unter der Meldung der Kontakt-Hinweis ("wenden Sie sich an uns") stehen soll. Default true,
     * weil die meisten Fehler technisch sind. Auf false setzen, wenn der Benutzer den Fehler selbst
     * beheben kann — z.B. ein Exposé, aus dem der Dienst nichts entnehmen konnte (#19159): dort ist
     * der Kontakt-Hinweis nur Rauschen.
     */
    readonly hasContactLink: boolean;

    constructor(errorKey: string, errorArgs?: Record<string, any> | null | undefined, hasContactLink = true) {
        super();
        this.message = errorKey;
        this.messageArgs = errorArgs ?? null;
        this.hasContactLink = hasContactLink;
    }
}
